// Dashboard.js - KPI Dashboard
import React, { useEffect, useState, useMemo, useRef } from "react";
import { database, ref, onValue, update, auth } from "./firebase";
import { 
  Users, Calendar,
  AlertCircle, Cake, FileText, CreditCard, BookOpen,
  UserX, Clock, Activity, DollarSign, X,
  ShoppingBag, TrendingUp, TrendingDown, UserPlus, UserCheck, Target,
  Eye, EyeOff
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { hasMinRole } from "./permissions";

/* =====================================================
   HELPERS
   ===================================================== */

function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getPeriodRange(period) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (period) {
    case "thisMonth":
      return {
        start: new Date(y, m, 1),
        end: now,
        label: now.toLocaleString("en-US", { month: "long", year: "numeric" }),
      };
    case "lastMonth": {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0, 23, 59, 59);
      return {
        start, end,
        label: start.toLocaleString("en-US", { month: "long", year: "numeric" }),
      };
    }
    case "lastQuarter": {
      const start = new Date(y, m - 3, 1);
      const end = new Date(y, m, 0, 23, 59, 59);
      return {
        start, end,
        label: `${start.toLocaleString("en-US", { month: "short" })} - ${end.toLocaleString("en-US", { month: "short", year: "numeric" })}`,
      };
    }
    case "lastYear":
      return {
        start: new Date(y - 1, 0, 1),
        end: new Date(y - 1, 11, 31, 23, 59, 59),
        label: `${y - 1}`,
      };
    default:
      return { start: new Date(y, m, 1), end: now, label: "This Month" };
  }
}

function isInRange(dateStr, start, end) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= start && d <= end;
}

function fmtMoney(n) {
  return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* =====================================================
   BIRTHDAY HELPERS
   ===================================================== */

function getUpcomingBirthdays(students, days = 30) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + days);
  const results = [];

  for (const student of students) {
    const raw = student.extraInfo?.birthday || student.birthday;
    if (!raw || typeof raw !== "string") continue;
    const parts = raw.split("/");
    if (parts.length !== 3) continue;
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    const birthYear = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(birthYear)) continue;

    let bday = new Date(today.getFullYear(), month, day);
    if (bday < today) bday = new Date(today.getFullYear() + 1, month, day);

    if (bday <= cutoff) {
      const age = bday.getFullYear() - birthYear;
      const daysUntil = Math.round((bday - today) / (1000 * 60 * 60 * 24));
      results.push({ ...student, upcomingDate: bday, turningAge: age, daysUntil });
    }
  }
  results.sort((a, b) => a.daysUntil - b.daysUntil);
  return results;
}

/* =====================================================
   DASHBOARD COMPONENT
   ===================================================== */

// Part of a Pro Shop sale paid with a gift card or account credit. That money was already
// counted as income when the gift card / credit was bought, so it isn't new income.
function creditPaidOn(txn) {
  const total = Number(txn.total || 0);
  const c = Number(txn.creditApplied || 0) || Number(txn.creditDeducted || 0) || (txn.paymentMethod === "account_credit" ? total : 0);
  return Math.max(0, Math.min(total, c));
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("thisMonth");

  // Raw data for KPI computations
  const [allStudentsRaw, setAllStudentsRaw] = useState([]);
  const [posTransactions, setPosTransactions] = useState([]);
  const [checkoutPageOrders, setCheckoutPageOrders] = useState([]);
  const [testingPayments, setTestingPayments] = useState([]);

  // Operational data
  const [userCount, setUserCount] = useState(0);
  const [upcomingTestings, setUpcomingTestings] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [pendingDrills, setPendingDrills] = useState(0);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [inactiveStudents, setInactiveStudents] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showRevenueReport, setShowRevenueReport] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null); // line clicked in the breakdown
  // Privacy toggle: hide the revenue figures on-screen (remembered across refreshes)
  const [revenueHidden, setRevenueHidden] = useState(() => {
    try { return localStorage.getItem("dashboard.revenueHidden") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("dashboard.revenueHidden", revenueHidden ? "1" : "0"); } catch {}
  }, [revenueHidden]);
  const [allStudents, setAllStudents] = useState([]);

  // memberSince migration ref
  const migrationDone = useRef(false);

  // ESC key to close revenue modal
  useEffect(() => {
    if (!showRevenueReport) return;
    const handleEsc = (e) => { if (e.key === "Escape") setShowRevenueReport(null); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showRevenueReport]);

  // Get current user profile
  useEffect(() => {
    if (!auth.currentUser) return;
    return onValue(ref(database, `users/${auth.currentUser.uid}`), snap => {
      setUserProfile(snap.val());
    });
  }, []);

  // Load students (main data source + memberSince migration)
  useEffect(() => {
    return onValue(ref(database, "students"), snap => {
      const val = snap.val() || {};
      const all = Object.entries(val)
        .filter(([, s]) => s.archived !== true)
        .map(([id, s]) => ({ id, ...s }));

      setAllStudentsRaw(all);

      setAllStudents(all.map(s => ({
        id: s.id,
        name: `${s.firstName || s.extraInfo?.firstName || ""} ${s.lastName || s.extraInfo?.lastName || ""}`.trim() || s.name || "",
        firstName: s.firstName || s.extraInfo?.firstName || "",
        lastName: s.lastName || s.extraInfo?.lastName || "",
        phone: s.extraInfo?.parent1Phone || s.phone || s.parentPhone || "",
      })));

      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      let inactive = 0;
      all.forEach(s => {
        if (!s.lastCheckIn) return;
        if (new Date(s.lastCheckIn) < cutoff) inactive++;
      });
      setInactiveStudents(inactive);
      setUpcomingBirthdays(getUpcomingBirthdays(all, 30));

      // One-time memberSince backfill
      if (!migrationDone.current) {
        migrationDone.current = true;
        const needsMigration = all.filter(s => s.member === true && !s.memberSince);
        if (needsMigration.length > 0) {
          const updates = {};
          const today = toLocalDateStr(new Date());
          needsMigration.forEach(s => { updates[`students/${s.id}/memberSince`] = today; });
          update(ref(database), updates).catch(() => {});
        }
      }
    });
  }, []);

  // Count users
  useEffect(() => {
    return onValue(ref(database, "users"), snap => {
      setUserCount(Object.keys(snap.val() || {}).length);
    });
  }, []);

  // Get upcoming testings
  useEffect(() => {
    return onValue(ref(database, "cycles"), snap => {
      const cycles = snap.val() || {};
      const allT = [];
      Object.entries(cycles).forEach(([cycleId, cycle]) => {
        if (cycle.testings) {
          Object.entries(cycle.testings).forEach(([testingId, testing]) => {
            allT.push({
              id: testingId, cycleId, title: testing.title,
              date: testing.promotionDate,
              testerCount: testing.testers ? Object.keys(testing.testers).length : 0,
            });
          });
        }
      });
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const future = allT.filter(t => t.date && new Date(t.date) >= today);
      future.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setUpcomingTestings(future.slice(0, 7));
    });
  }, []);

  // Get upcoming events from announcements
  useEffect(() => {
    return onValue(ref(database, "announcements"), snap => {
      const val = snap.val() || {};
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const future = [];
      Object.entries(val).forEach(([id, a]) => {
        const dateStr = a.eventDates ? a.eventDates[0] : a.eventDate;
        if (!dateStr) return;
        const [y, m, d] = dateStr.split("-").map(Number);
        const evDate = new Date(y, m - 1, d); evDate.setHours(0, 0, 0, 0);
        const lastDateStr = a.eventDates ? a.eventDates[a.eventDates.length - 1] : a.eventDate;
        const [ly, lm, ld] = lastDateStr.split("-").map(Number);
        const lastDate = new Date(ly, lm - 1, ld); lastDate.setHours(23, 59, 59, 999);
        if (lastDate >= today) {
          future.push({ id, title: a.title || "Untitled", category: a.category || "event", date: evDate, dateStr });
        }
      });
      future.sort((a, b) => a.date - b.date);
      setUpcomingEvents(future.slice(0, 10));
    });
  }, []);

  // Count pending drill approvals
  useEffect(() => {
    return onValue(ref(database, "drillEditsPending"), snap => {
      setPendingDrills(Object.keys(snap.val() || {}).length);
    });
  }, []);

  // Load testing registrations
  useEffect(() => {
    return onValue(ref(database, "testingRegistrations"), snap => {
      const val = snap.val() || {};
      const payments = [];
      const pending = [];
      const activity = [];
      const now = new Date();

      Object.entries(val).forEach(([regId, reg]) => {
        if (!reg.signups) return;
        Object.entries(reg.signups).forEach(([signupId, signup]) => {
          if ((signup.status === "pending_payment" || signup.paymentStatus === "pending")
            && signup.status !== "cancelled" && signup.status !== "registered"
            && (signup.totalAmount || 0) > 0) {
            pending.push({
              regId, signupId, email: signup.email,
              students: (signup.students || []).map(s => s.name).join(", "),
              amount: signup.totalAmount, regTitle: reg.title, createdAt: signup.createdAt,
            });
          }

          const isPaid = signup.paymentStatus === "paid" || (signup.status === "registered" && (signup.totalAmount || 0) === 0);
          if (isPaid && signup.status !== "cancelled") {
            const paidDate = signup.paidAt || signup.createdAt;
            if (paidDate) {
              payments.push({
                date: new Date(paidDate), dateStr: toLocalDateStr(new Date(paidDate)),
                amount: Number(signup.paidAmount || signup.totalAmount || 0),
                students: (signup.students || []).map(s => s.name).join(", "),
                email: signup.email, regTitle: reg.title || "Testing",
                couponCode: signup.couponCode || null, couponDiscount: signup.couponDiscount || 0,
                source: "testing",
                lineItems: (signup.students || []).map(s => ({
                  name: `${s.name}${s.reportedRank || s.rank ? ` — ${s.reportedRank || s.rank}` : ""}`,
                  qty: 1,
                  amount: Number(s.customPrice || s.testingFee || 0),
                })),
              });
            }
          }

          if (signup.createdAt) {
            const created = new Date(signup.createdAt);
            if ((now - created) / 86400000 <= 7 && signup.status !== "cancelled") {
              const studentNames = (signup.students || []).map(s => s.name).join(", ");
              activity.push({
                type: signup.paymentStatus === "paid" ? "payment" : "registration",
                message: signup.paymentStatus === "paid"
                  ? `${studentNames} paid $${Number(signup.paidAmount || signup.totalAmount || 0).toFixed(0)} for ${reg.title || "testing"}`
                  : `${studentNames} registered for ${reg.title || "testing"}`,
                date: created, icon: signup.paymentStatus === "paid" ? "payment" : "signup",
              });
            }
          }
        });
      });

      setTestingPayments(payments);
      setPendingPayments(pending);
      setRecentActivity(prev => {
        const merged = [...prev.filter(a => a.type !== "payment" && a.type !== "registration"), ...activity];
        merged.sort((a, b) => b.date - a.date);
        return merged.slice(0, 15);
      });
    });
  }, []);

  // Load POS transactions
  useEffect(() => {
    return onValue(ref(database, "posTransactions"), snap => {
      const val = snap.val() || {};
      const now = new Date();
      const items = [];
      const posActivity = [];

      Object.entries(val).forEach(([, txn]) => {
        if (txn.status === "voided") return;
        const d = new Date(txn.createdAt);
        // Gift card / account credit was already counted as income when it was bought
        const creditPaid = creditPaidOn(txn);
        items.push({
          date: d, dateStr: toLocalDateStr(d),
          amount: Math.round((Number(txn.total || 0) - creditPaid) * 100) / 100,
          orderTotal: Number(txn.total || 0), creditPaid,
          students: txn.customerName || "Walk-in",
          regTitle: "Pro Shop", source: "pos",
          paymentMethod: txn.paymentMethod,
          itemCount: (txn.items || []).length,
          subtotal: txn.subtotal, taxAmount: txn.taxAmount,
          lineItems: (txn.items || []).map(i => ({
            name: `${i.name || "Item"}${i.size ? ` (${i.size})` : ""}${i.color ? ` - ${i.color}` : ""}`,
            qty: Number(i.quantity || 1),
            amount: Number(i.lineTotal != null ? i.lineTotal : (i.price || 0) * Number(i.quantity || 1)),
          })),
        });

        if ((now - d) / 86400000 <= 7) {
          const itemNames = (txn.items || []).map(i => i.name).slice(0, 2).join(", ");
          const extra = (txn.items || []).length > 2 ? ` +${(txn.items || []).length - 2} more` : "";
          posActivity.push({
            type: "pos_sale",
            message: `${txn.customerName || "Walk-in"} purchased ${itemNames}${extra} - $${Number(txn.total || 0).toFixed(2)}`,
            date: d, icon: "pos",
          });
        }
      });

      setPosTransactions(items);
      setRecentActivity(prev => {
        const merged = [...prev.filter(a => a.type !== "pos_sale"), ...posActivity];
        merged.sort((a, b) => b.date - a.date);
        return merged.slice(0, 20);
      });
    });
  }, []);

  // Load Checkout Page orders (summer uniforms, etc.) — counted as Pro Shop income
  useEffect(() => {
    return onValue(ref(database, "checkoutOrders"), snap => {
      const val = snap.val() || {};
      const now = new Date();
      const items = [];
      const checkoutActivity = [];

      // checkoutOrders is nested by pageId, so iterate two levels deep
      Object.entries(val).forEach(([, ordersByPage]) => {
        Object.entries(ordersByPage || {}).forEach(([, order]) => {
          // Only count orders that have actually been paid
          const paidStatuses = ["paid", "paid_credit"];
          if (!paidStatuses.includes(order.paymentStatus)) return;
          // Skip orders that already have a POS transaction (avoid double-counting)
          if (order.posTransactionId) return;

          const d = new Date(order.paidAt || order.createdAt);
          const creditPaid = Math.min(Number(order.total || 0), Number(order.creditApplied || 0) || (order.paymentStatus === "paid_credit" ? Number(order.total || 0) : 0));
          items.push({
            date: d, dateStr: toLocalDateStr(d),
            amount: Math.round((Number(order.total || 0) - creditPaid) * 100) / 100,
            orderTotal: Number(order.total || 0), creditPaid,
            students: order.contactName || "Customer",
            regTitle: order.pageTitle || "Pro Shop", source: "pos",
            paymentMethod: order.paymentStatus === "paid_credit" ? "credit" : (order.paymentMethod || "card_stripe"),
            itemCount: (order.items || []).length,
            subtotal: order.subtotal, taxAmount: order.taxAmount,
            lineItems: (order.items || []).map(i => ({
              name: `${i.itemName || i.name || "Item"}${i.size ? ` (${i.size})` : ""}${i.color ? ` - ${i.color}` : ""}${i.forName ? ` — ${i.forName}` : ""}`,
              qty: Number(i.quantity || 1),
              amount: Number(i.lineTotal != null ? i.lineTotal : (i.price || 0) * Number(i.quantity || 1)),
            })),
          });

          if ((now - d) / 86400000 <= 7) {
            const itemNames = (order.items || []).map(i => i.itemName).slice(0, 2).join(", ");
            const extra = (order.items || []).length > 2 ? ` +${(order.items || []).length - 2} more` : "";
            checkoutActivity.push({
              type: "pos_sale",
              message: `${order.contactName || "Customer"} ordered ${itemNames}${extra} - $${Number(order.total || 0).toFixed(2)}`,
              date: d, icon: "pos",
            });
          }
        });
      });

      setCheckoutPageOrders(items);
      setRecentActivity(prev => {
        // pos_sale activities come from BOTH posTransactions and checkoutOrders.
        // We rebuild the merged list each time either updates.
        const nonPosSale = prev.filter(a => a.type !== "pos_sale");
        const merged = [...nonPosSale, ...checkoutActivity];
        merged.sort((a, b) => b.date - a.date);
        return merged.slice(0, 20);
      });
    });
  }, []);

  // Generate action items
  useEffect(() => {
    const items = [];
    if (upcomingTestings.length > 0) {
      const next = upcomingTestings[0];
      const daysUntil = Math.ceil((new Date(next.date) - new Date()) / 86400000);
      if (daysUntil >= 0 && daysUntil <= 14) {
        items.push({ type: "warning", icon: <Calendar size={16} />,
          message: `${next.title}: ${next.testerCount} students testing in ${daysUntil} day${daysUntil !== 1 ? "s" : ""}`,
          action: () => navigate("/testings") });
      }
    }
    if (pendingDrills > 0) items.push({ type: "info", icon: <BookOpen size={16} />,
      message: `${pendingDrills} drill${pendingDrills !== 1 ? "s" : ""} pending approval`, action: () => navigate("/drill-approvals") });
    if (pendingPayments.length > 0) items.push({ type: "warning", icon: <CreditCard size={16} />,
      message: `${pendingPayments.length} registration${pendingPayments.length !== 1 ? "s" : ""} with pending payment`, action: () => navigate("/testings") });
    if (inactiveStudents > 0) items.push({ type: "alert", icon: <UserX size={16} />,
      message: `${inactiveStudents} student${inactiveStudents !== 1 ? "s" : ""} haven't attended in 30+ days`, action: () => navigate("/students") });
    const todayBdays = upcomingBirthdays.filter(b => b.daysUntil === 0);
    if (todayBdays.length > 0) items.push({ type: "celebration", icon: <Cake size={16} />,
      message: `${todayBdays.map(b => b.name || b.extraInfo?.firstName || "Student").join(", ")} ${todayBdays.length === 1 ? "has a" : "have"} birthday today!`, action: null });
    setActionItems(items);
  }, [upcomingTestings, pendingDrills, pendingPayments, inactiveStudents, upcomingBirthdays, navigate]);

  /* ═══════ COMPUTED KPIs ═══════ */

  const periodRange = useMemo(() => getPeriodRange(period), [period]);

  const incomeKPIs = useMemo(() => {
    const { start, end } = periodRange;
    const tp = testingPayments.filter(p => p.date >= start && p.date <= end);
    const pp = posTransactions.filter(p => p.date >= start && p.date <= end);
    const cp = checkoutPageOrders.filter(p => p.date >= start && p.date <= end);
    const testingTotal = tp.reduce((s, p) => s + p.amount, 0);
    const posTotal = pp.reduce((s, p) => s + p.amount, 0) + cp.reduce((s, p) => s + p.amount, 0);
    const allItems = [...tp, ...pp, ...cp].sort((a, b) => b.date - a.date);
    return { total: testingTotal + posTotal, testingTotal, posTotal, allItems, txnCount: allItems.length };
  }, [testingPayments, posTransactions, checkoutPageOrders, periodRange]);

  // End-of-day: money taken in TODAY (calendar day), independent of the
  // This Month / Last Month period tabs. Powers the "Today's Income" card
  // and its clickable breakdown.
  const todayKPIs = useMemo(() => {
    const todayStr = toLocalDateStr(new Date());
    const tp = testingPayments.filter(p => p.dateStr === todayStr);
    const pp = posTransactions.filter(p => p.dateStr === todayStr);
    const cp = checkoutPageOrders.filter(p => p.dateStr === todayStr);
    const testingTotal = tp.reduce((s, p) => s + p.amount, 0);
    const posTotal = pp.reduce((s, p) => s + p.amount, 0) + cp.reduce((s, p) => s + p.amount, 0);
    const allItems = [...tp, ...pp, ...cp].sort((a, b) => b.date - a.date);
    return { total: testingTotal + posTotal, testingTotal, posTotal, allItems, txnCount: allItems.length };
  }, [testingPayments, posTransactions, checkoutPageOrders]);

  const pipelineKPIs = useMemo(() => {
    const { start, end } = periodRange;
    const members = allStudentsRaw.filter(s => s.member === true);
    const leads = allStudentsRaw.filter(s => s.memberStatus === "Lead");
    const trials = allStudentsRaw.filter(s => s.memberStatus === "On a Trial");
    const newMembers = members.filter(s => s.memberSince && isInRange(s.memberSince, start, end));
    const departures = allStudentsRaw.filter(s => s.member === false && s.memberEndDate && isInRange(s.memberEndDate, start, end));
    return { totalMembers: members.length, newMembers: newMembers.length, leads: leads.length,
      trials: trials.length, departures: departures.length, netGrowth: newMembers.length - departures.length };
  }, [allStudentsRaw, periodRange]);

  const whatsComingUp = useMemo(() => {
    const items = [];
    upcomingTestings.forEach(t => {
      const [y, m, d] = (t.date || "").split("-").map(Number);
      items.push({ id: `testing-${t.id}`, title: t.title,
        subtitle: `${t.testerCount} student${t.testerCount !== 1 ? "s" : ""}`,
        category: "testing", date: new Date(y, m - 1, d), dateStr: t.date, route: "/testings" });
    });
    upcomingEvents.forEach(ev => {
      items.push({ id: `event-${ev.id}`, title: ev.title, subtitle: null,
        category: ev.category, date: ev.date, dateStr: ev.dateStr, route: "/announcements" });
    });
    items.sort((a, b) => a.date - b.date);
    return items.slice(0, 8);
  }, [upcomingTestings, upcomingEvents]);


  /* ═══════ ROLE-BASED VISIBILITY ═══════ */
  const canSeeFinancials = hasMinRole(userProfile?.role, "admin");

  /* ═══════ RENDER ═══════ */

  return (
    <div className="page-content">
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 16px" }}>
        
        {/* Welcome Header */}
        <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: 4 }}>Welcome back, {userProfile?.name || "User"}!</h2>
            <div style={{ color: "#666", fontSize: 15 }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <div style={{ display: "flex", gap: 6, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { key: "thisMonth", label: "This Month" },
            { key: "lastMonth", label: "Last Month" },
            { key: "lastQuarter", label: "Last Quarter" },
            { key: "lastYear", label: "Last Year" },
          ].map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              style={{
                padding: "8px 18px", fontSize: 13, fontWeight: 600,
                background: period === p.key ? "#1e293b" : "#f1f5f9",
                color: period === p.key ? "#fff" : "#475569",
                border: "none", borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
              }}
            >{p.label}</button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 13, color: "#94a3b8", alignSelf: "center" }}>{periodRange.label}</span>
        </div>

        {/* ═══════ INCOME ROW ═══════ */}
        {canSeeFinancials && (
        <>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button
            onClick={() => setRevenueHidden(h => !h)}
            title={revenueHidden ? "Show revenue" : "Hide revenue from view"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", fontSize: 13, fontWeight: 600,
              background: "#f1f5f9", color: "#475569",
              border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer",
            }}
          >
            {revenueHidden ? <><Eye size={15} /> Show revenue</> : <><EyeOff size={15} /> Hide revenue</>}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, marginBottom: 20 }}>
          {/* Month-to-date / selected period — click for breakdown */}
          <div onClick={() => { if (!revenueHidden) setShowRevenueReport("period"); }}
            style={{ ...kpiCardStyle, background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "#fff", cursor: revenueHidden ? "default" : "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Income · {periodRange.label}</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>{revenueHidden ? "••••••" : fmtMoney(incomeKPIs.total)}</div>
              </div>
              <DollarSign size={28} style={{ opacity: 0.4 }} />
            </div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
              {revenueHidden
                ? "Hidden — tap Show revenue"
                : <>{incomeKPIs.txnCount} transaction{incomeKPIs.txnCount !== 1 ? "s" : ""} · <span style={{ textDecoration: "underline" }}>view breakdown</span></>}
            </div>
          </div>

          {/* Today's income — end of day, click for breakdown */}
          <div onClick={() => { if (!revenueHidden) setShowRevenueReport("today"); }}
            style={{ ...kpiCardStyle, background: "#fff", cursor: revenueHidden ? "default" : "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={kpiLabelStyle}>Today's Income · {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginTop: 4 }}>{revenueHidden ? "••••••" : fmtMoney(todayKPIs.total)}</div>
              </div>
              <div style={{ ...iconBubbleStyle, background: "#f0fdf4" }}><DollarSign size={20} color="#059669" /></div>
            </div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 8 }}>
              {revenueHidden
                ? "Hidden"
                : <>{todayKPIs.txnCount} transaction{todayKPIs.txnCount !== 1 ? "s" : ""} today · <span style={{ textDecoration: "underline", color: "#059669" }}>view breakdown</span></>}
            </div>
          </div>
        </div>
        </>
        )}

        {/* ═══════ PIPELINE ROW ═══════ */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
          <div onClick={() => navigate("/students?status=member")} style={{ ...kpiCardStyle, background: "#fff", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={kpiLabelStyle}>Active Members</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginTop: 4 }}>{pipelineKPIs.totalMembers}</div>
              </div>
              <div style={{ ...iconBubbleStyle, background: "#eff6ff" }}><Users size={20} color="#2563eb" /></div>
            </div>
          </div>

          <div style={{ ...kpiCardStyle, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={kpiLabelStyle}>New Members</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginTop: 4 }}>{pipelineKPIs.newMembers}</div>
              </div>
              <div style={{ ...iconBubbleStyle, background: "#f0fdf4" }}><UserPlus size={20} color="#16a34a" /></div>
            </div>
            {pipelineKPIs.netGrowth !== 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 13, fontWeight: 600,
                color: pipelineKPIs.netGrowth > 0 ? "#16a34a" : "#dc2626" }}>
                {pipelineKPIs.netGrowth > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                Net {pipelineKPIs.netGrowth > 0 ? "+" : ""}{pipelineKPIs.netGrowth}
                <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 2 }}>({pipelineKPIs.departures} left)</span>
              </div>
            )}
          </div>

          <div onClick={() => navigate("/students?status=lead")} style={{ ...kpiCardStyle, background: "#fff", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={kpiLabelStyle}>Leads</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginTop: 4 }}>{pipelineKPIs.leads}</div>
              </div>
              <div style={{ ...iconBubbleStyle, background: "#fefce8" }}><Target size={20} color="#ca8a04" /></div>
            </div>
          </div>

          <div onClick={() => navigate("/students?status=trial")} style={{ ...kpiCardStyle, background: "#fff", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={kpiLabelStyle}>Trials</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginTop: 4 }}>{pipelineKPIs.trials}</div>
              </div>
              <div style={{ ...iconBubbleStyle, background: "#fdf2f8" }}><UserCheck size={20} color="#db2777" /></div>
            </div>
          </div>
        </div>

        {/* ═══════ OPERATIONAL CARDS ═══════ */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, marginBottom: 20 }}>

          {/* What's Coming Up */}
          <div style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}><Calendar size={20} color="#1e88e5" /> What's Coming Up</h3>
            {whatsComingUp.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {whatsComingUp.map(item => {
                  const catColors = { event: "#1e88e5", testing: "#f57c00", news: "#43a047", tournament: "#8e24aa" };
                  const color = catColors[item.category] || "#757575";
                  const todayStr = toLocalDateStr(new Date());
                  const isToday = item.dateStr === todayStr;
                  return (
                    <div key={item.id} onClick={() => navigate(item.route)}
                      style={{ padding: 12, background: isToday ? "#e3f2fd" : "#fafafa", borderRadius: 8, borderLeft: `4px solid ${color}`, cursor: "pointer", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                      onMouseLeave={e => e.currentTarget.style.background = isToday ? "#e3f2fd" : "#fafafa"}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</div>
                          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                            {item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}{" \u00B7 "}
                            <span style={{ color, fontWeight: 600, textTransform: "capitalize" }}>{item.category}</span>
                            {item.subtitle && <span> {"\u00B7"} {item.subtitle}</span>}
                          </div>
                        </div>
                        {isToday && <span style={{ fontSize: 11, fontWeight: 700, color: "#1e88e5", background: "#bbdefb", padding: "2px 8px", borderRadius: 4, flexShrink: 0, marginLeft: 8 }}>Today</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div style={{ color: "#999", textAlign: "center", padding: 20 }}>Nothing coming up</div>}
          </div>

          {/* Upcoming Birthdays */}
          <div style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>
              <Cake size={20} color="#e91e63" /> Upcoming Birthdays
              {upcomingBirthdays.length > 0 && <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#e91e63" }}>{upcomingBirthdays.length}</span>}
            </h3>
            {upcomingBirthdays.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {upcomingBirthdays.map((student, idx) => (
                  <div key={student.id || idx} style={{
                    padding: 12, background: student.daysUntil === 0 ? "#fce4ec" : "#fafafa", borderRadius: 8,
                    borderLeft: student.daysUntil === 0 ? "4px solid #e91e63" : student.daysUntil <= 7 ? "4px solid #ff9800" : "4px solid #e0e0e0",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {student.extraInfo?.firstName || student.name?.split(" ")[0] || ""} {student.extraInfo?.lastName || student.name?.split(" ").slice(1).join(" ") || ""}
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {student.upcomingDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} {"\u00B7"} Turning {student.turningAge}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600,
                        color: student.daysUntil === 0 ? "#e91e63" : student.daysUntil <= 7 ? "#ff9800" : "#666" }}>
                        {student.daysUntil === 0 ? "Today!" : student.daysUntil === 1 ? "Tomorrow" : `${student.daysUntil} days`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: "#999", textAlign: "center", padding: 20 }}>No birthdays in the next 30 days</div>}
          </div>

          {/* Needs Attention */}
          <div style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}><AlertCircle size={20} /> Needs Attention</h3>
            {actionItems.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {actionItems.map((item, idx) => {
                  const ts = { warning: { bg: "#fff7ed", border: "#f97316", ic: "#ea580c" }, alert: { bg: "#fef2f2", border: "#ef4444", ic: "#dc2626" },
                    info: { bg: "#eff6ff", border: "#3b82f6", ic: "#2563eb" }, celebration: { bg: "#fdf2f8", border: "#ec4899", ic: "#db2777" } };
                  const s = ts[item.type] || ts.info;
                  return (
                    <div key={idx} style={{ padding: "12px 14px", background: s.bg, borderRadius: 8, borderLeft: `4px solid ${s.border}`,
                      cursor: item.action ? "pointer" : "default", display: "flex", alignItems: "center", gap: 10, transition: "opacity 0.15s" }}
                      onClick={item.action || undefined}
                      onMouseEnter={e => { if (item.action) e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                      <span style={{ color: s.ic, flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ fontSize: 14, color: "#333" }}>{item.message}</div>
                    </div>
                  );
                })}
              </div>
            ) : <div style={{ color: "#999", textAlign: "center", padding: 20 }}>{"\u2705"} Everything looks good!</div>}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ ...sectionCardStyle, marginBottom: 24 }}>
          <h3 style={sectionTitleStyle}><Activity size={20} color="#6366f1" /> Recent Activity</h3>
          {(() => {
            const visibleActivity = canSeeFinancials
              ? recentActivity
              : recentActivity.filter(a => a.type !== "pos_sale" && a.type !== "payment");
            return visibleActivity.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {visibleActivity.slice(0, showAllActivity ? 20 : 5).map((item, idx) => {
                const iconMap = { payment: <CreditCard size={14} color="#059669" />, signup: <FileText size={14} color="#2563eb" />,
                  registration: <FileText size={14} color="#2563eb" />, pos: <ShoppingBag size={14} color="#7c3aed" /> };
                return (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
                    borderBottom: idx < Math.min(visibleActivity.length, showAllActivity ? 20 : 5) - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <span style={{ flexShrink: 0 }}>{iconMap[item.icon] || <Clock size={14} color="#6b7280" />}</span>
                    <div style={{ flex: 1, fontSize: 14, color: "#374151" }}>{item.message}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{getTimeAgo(item.date)}</div>
                  </div>
                );
              })}
              {visibleActivity.length > 5 && (
                <button onClick={() => setShowAllActivity(!showAllActivity)}
                  style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 0 0", textAlign: "center" }}>
                  {showAllActivity ? "Show Less" : `Show All (${visibleActivity.length})`}
                </button>
              )}
            </div>
          ) : <div style={{ color: "#999", textAlign: "center", padding: 20 }}>No recent activity</div>;
          })()}
        </div>

        {/* ═══════ REVENUE DETAIL MODAL ═══════ */}
        {canSeeFinancials && showRevenueReport && (() => {
          const isToday = showRevenueReport === "today";
          const data = isToday ? todayKPIs : incomeKPIs;
          const heading = isToday
            ? `Today's Income · ${new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}`
            : `Income Detail · ${periodRange.label}`;
          return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
            onClick={() => setShowRevenueReport(null)}>
            <div style={{ background: "#fff", borderRadius: 16, padding: 0, maxWidth: 600, width: "90%", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <DollarSign size={20} color="#059669" /> {heading}
                </h3>
                <button onClick={() => setShowRevenueReport(null)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "#6b7280" }}><X size={20} /></button>
              </div>
              <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                  <div style={{ background: "#f0fdf4", borderRadius: 10, padding: 14, textAlign: "center" }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Total</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#059669" }}>{fmtMoney(data.total)}</div>
                  </div>
                  <div style={{ background: "#eff6ff", borderRadius: 10, padding: 14, textAlign: "center" }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Testing</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#2563eb" }}>{fmtMoney(data.testingTotal)}</div>
                  </div>
                  <div style={{ background: "#f5f3ff", borderRadius: 10, padding: 14, textAlign: "center" }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Pro Shop</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#7c3aed" }}>{fmtMoney(data.posTotal)}</div>
                  </div>
                </div>

                {data.allItems.length === 0 ? (
                  <div style={{ color: "#999", textAlign: "center", padding: 20 }}>{isToday ? "No money taken in yet today" : "No transactions for this period"}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {data.allItems.map((li, idx) => (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0",
                        borderBottom: idx < data.allItems.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: 14, color: "#1f2937" }}>{li.students}</div>
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                            {li.source === "pos" && <span style={{ background: "#f5f3ff", color: "#7c3aed", fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>PRO SHOP</span>}
                            {li.regTitle}{" \u00B7 "}{li.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            {li.paymentMethod && <span style={{ color: "#6b7280" }}>{" \u00B7 "}{li.paymentMethod}</span>}
                            {li.couponCode && <span style={{ color: "#7c3aed" }}>{" \u00B7 "}{li.couponCode} (-${li.couponDiscount.toFixed(2)})</span>}
                            {li.creditPaid > 0 && <span style={{ color: "#b45309" }}>{" \u00B7 "}${li.creditPaid.toFixed(2)} gift card/credit not counted</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => setOrderDetail(li)}
                          title="View order details"
                          style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 15, color: li.amount > 0 ? "#059669" : "#6b7280", flexShrink: 0, marginLeft: 12, textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>
                          ${li.amount.toFixed(2)}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}

        {/* ═══════ SINGLE ORDER DETAIL (invoice) ═══════ */}
        {canSeeFinancials && orderDetail && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
            onClick={() => setOrderDetail(null)}>
            <div style={{ background: "#fff", borderRadius: 16, maxWidth: 480, width: "90%", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 17 }}>{orderDetail.students}</h3>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
                    {orderDetail.source === "pos" && <span style={{ background: "#f5f3ff", color: "#7c3aed", fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>PRO SHOP</span>}
                    {orderDetail.regTitle}{" · "}{orderDetail.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {orderDetail.paymentMethod && <span>{" · "}{orderDetail.paymentMethod}</span>}
                  </div>
                </div>
                <button onClick={() => setOrderDetail(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 4 }}><X size={20} /></button>
              </div>
              <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
                {(orderDetail.lineItems && orderDetail.lineItems.length) ? (
                  <>
                    {orderDetail.lineItems.map((it, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6", gap: 12 }}>
                        <div style={{ fontSize: 14, color: "#1f2937" }}>
                          {it.qty > 1 && <span style={{ color: "#9ca3af", fontWeight: 600, marginRight: 6 }}>{it.qty}×</span>}
                          {it.name}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#374151", flexShrink: 0 }}>${Number(it.amount || 0).toFixed(2)}</div>
                      </div>
                    ))}
                    {orderDetail.subtotal != null && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280", paddingTop: 10 }}>
                        <span>Subtotal</span><span>${Number(orderDetail.subtotal).toFixed(2)}</span>
                      </div>
                    )}
                    {orderDetail.taxAmount != null && Number(orderDetail.taxAmount) > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280", paddingTop: 4 }}>
                        <span>Tax</span><span>${Number(orderDetail.taxAmount).toFixed(2)}</span>
                      </div>
                    )}
                    {orderDetail.couponCode && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#7c3aed", paddingTop: 4 }}>
                        <span>{orderDetail.couponCode}</span><span>-${Number(orderDetail.couponDiscount || 0).toFixed(2)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: "#9ca3af", textAlign: "center", padding: "10px 0 18px" }}>No itemized detail saved for this order.</div>
                )}
                {orderDetail.creditPaid > 0 && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151", paddingTop: 10 }}>
                      <span>Order total</span><span>${Number(orderDetail.orderTotal || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#b45309", paddingTop: 4 }}>
                      <span>Paid with gift card / account credit (counted when it was bought)</span><span>-${Number(orderDetail.creditPaid).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 14, borderTop: "2px solid #e5e7eb" }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{orderDetail.creditPaid > 0 ? "Counted as income" : "Total"}</span>
                  <span style={{ fontWeight: 700, fontSize: 18, color: "#059669" }}>${Number(orderDetail.amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ═══════ STYLE CONSTANTS ═══════ */

const kpiCardStyle = {
  borderRadius: 12, padding: "20px 22px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9",
};

const kpiLabelStyle = {
  fontSize: 12, fontWeight: 600, color: "#94a3b8",
  textTransform: "uppercase", letterSpacing: 0.5,
};

const iconBubbleStyle = {
  width: 40, height: 40, borderRadius: 10,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const sectionCardStyle = {
  background: "#fff", borderRadius: 12, padding: 22,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9",
};

const sectionTitleStyle = {
  margin: "0 0 16px 0", display: "flex", alignItems: "center", gap: 8, fontSize: 16,
};
